/**
 * The ACTION modal's input forms — the tap side of an earn card when the
 * member has something to complete — one per kind of
 * action a member completes. Each is only an INPUT DOOR into the
 * existing machinery: it collects the member's payload (a serial, a link, a
 * platform), submits to the generic /earn endpoint keyed by the card's
 * objectiveTemplateId, and reports what came back. Validation, policy,
 * dedup, event emission, scoring — all server-side; nothing here knows what
 * anything pays.
 *
 * They share the same three-state shape (form → submitting → outcome) and
 * the same classes, because a member should not have to re-learn a form per
 * capability. Copy distinguishes them; structure does not. The pieces they
 * all use live in `actionFormShell`; the referral action has its own file.
 */
import React, {FormEvent, useEffect, useId, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {boardEarnObjectivesKey} from "@/hooks/ledger-boards/useGetBoardEarnObjectives";
import {
    useVerifyEarnSerial,
    useCreateEarnSocialFollow,
    useCreateEarnSocialLike,
    useCreateEarnSocialPost,
    useGetEarnActionPolicy,
    earnActionPolicyKey,
} from "@/hooks/earn/useEarnActions";
import {
    PlatformPills,
    RemainingLine,
    PolicyStatus,
    platformMeta,
    asHashtag,
    actionErrorMessage,
    type ActionOutcome,
} from "./actionFormShell";
import {ExternalLink} from "@/widgets/ExternalLink";
import useHasFocus from "@/hooks/useHasFocus";
import "./earnActionForms.scss";

/** What every action form takes: its objective, the card's label, a refresh callback. */
interface EarnActionFormProps {
    objectiveTemplateId: string;
    label?: string;
    onClaimed?: () => void;
}


/** Serial / registered-product entry — "Serial off the shaft or the box." */
export function SerialActionForm({objectiveTemplateId, label, onClaimed}: EarnActionFormProps): React.ReactElement {
    /* useId, not a static string — two same-kind widgets on one page must not share an id. */
    const inputId = useId();
    const [serial, setSerial] = useState("");
    const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
    const verify = useVerifyEarnSerial();
    /* Same gate as the other forms — signed-out gets "sign in", not a dead submit. */
    const {data: policy, isLoading: policyLoading, isError: policyFailed} = useGetEarnActionPolicy(objectiveTemplateId);

    const submit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (!serial.trim() || verify.isPending) return;
        setOutcome(null);
        try {
            const result = await verify.mutateAsync({objectiveTemplateId, serialNumber: serial.trim()});
            if (result.alreadyClaimed) {
                setOutcome({tone: "info", text: "That serial has already been claimed."});
            } else {
                setOutcome({tone: "ok", text: "Verified — points are on the way."});
                onClaimed?.();
            }
        } catch (err) {
            setOutcome({tone: "error", text: await actionErrorMessage(err)});
        }
    };

    if (!policy) {
        return (
            <div className="earn-action earn-action__stack">
                <PolicyStatus isLoading={policyLoading} isError={policyFailed} />
            </div>
        );
    }

    return (
        <form className="earn-action" onSubmit={submit}>
            <label className="earn-action__label" htmlFor={inputId}>
                {"Enter your serial number"}
            </label>
            <div className="earn-action__row">
                <input
                    id={inputId}
                    className="earn-action__input"
                    value={serial}
                    onChange={e => setSerial(e.target.value)}
                    placeholder="Serial number"
                    autoComplete="off"
                />
                <button className="earn-action__submit" type="submit" disabled={!serial.trim() || verify.isPending}>
                    {verify.isPending ? "Checking…" : "Verify"}
                </button>
            </div>
            {outcome && <p className={`earn-action__outcome earn-action__outcome--${outcome.tone}`} role="status">{outcome.text}</p>}
        </form>
    );
}

/**
 * Follow claim — open the account, follow, come back and claim.
 *
 * The platform comes from the assignment; the member is not asked to type it,
 * which is what the free-text field used to demand of them.
 */
export function FollowActionForm({objectiveTemplateId, onClaimed}: EarnActionFormProps): React.ReactElement {
    const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
    const follow = useCreateEarnSocialFollow();
    const {data: policy, isLoading: policyLoading, isError: policyFailed} = useGetEarnActionPolicy(objectiveTemplateId);
    const queryClient = useQueryClient();

    const platforms = policy?.acceptedPlatforms ?? [];
    const meta = platformMeta(platforms[0] ?? "");
    /* The account to open — the objective's own url. Post links never belong here. */
    const profileUrl = policy?.accountUrl;

    const claim = async (): Promise<void> => {
        const chosen = platforms[0];
        if (!chosen || follow.isPending) return;
        setOutcome(null);
        try {
            const result = await follow.mutateAsync({objectiveTemplateId, platform: chosen});
            if (result.alreadyClaimed) {
                setOutcome({tone: "info", text: "You've already claimed this follow."});
            } else {
                setOutcome({tone: "ok", text: "Claimed — points are on the way."});
                onClaimed?.();
            }
            void queryClient.invalidateQueries({queryKey: earnActionPolicyKey(objectiveTemplateId)});
            void queryClient.invalidateQueries({queryKey: boardEarnObjectivesKey});
        } catch (err) {
            setOutcome({tone: "error", text: await actionErrorMessage(err)});
        }
    };

    /*
     * NO POLICY, NO CARD. Without it there is no platform and no account to
     * open, and the full form still rendered: a lede reading "Follow on  to
     * earn points … open the account below" with the platform missing and no
     * link beneath it, over a claim button. That tells a member to do
     * something impossible and then offers to reward them for it.
     */
    if (!policy) {
        return (
            <div className="earn-action earn-action__stack">
                <PolicyStatus isLoading={policyLoading} isError={policyFailed} />
            </div>
        );
    }

    return (
        <div className="earn-action earn-action__stack">
            <p className="earn-action__lede">
                {policy.instructions ??
                    `Follow on ${meta.label} to earn points this period. Open the account below, follow, then come back and claim.`}
            </p>
            {profileUrl &&
                <ExternalLink className="earn-action__action" href={profileUrl}>
                    Follow on {meta.label}
                </ExternalLink>
            }
            {/* No account link = nothing the member can actually do — never offer the button anyway. */}
            {!profileUrl &&
                <p className="earn-action__outcome earn-action__outcome--info" role="status">
                    This one isn&apos;t ready to claim yet. Check back soon.
                </p>
            }
            <button
                type="button"
                className="earn-action__action"
                onClick={() => void claim()}
                disabled={!platforms.length || !profileUrl || follow.isPending}
            >
                {follow.isPending ? "Claiming…" : "Claim my points"}
            </button>
            {outcome && <p className={`earn-action__outcome earn-action__outcome--${outcome.tone}`} role="status">{outcome.text}</p>}
        </div>
    );
}

/**
 * Like claim.
 *
 * TWO STEPS, NEVER A TEXT FIELD. The member opens the post the campaign named,
 * likes it, and comes back to confirm. They are never asked to find and paste
 * a URL for a post the brand chose — a ruling, not a preference.
 *
 * The raw link is offered underneath because an in-app browser will sometimes
 * swallow the button, and a member who cannot open the post cannot claim.
 *
 * WITH NO NAMED POST there is nothing to like and nothing to confirm, so the
 * card says so. It used to fall back to asking for the URL, which is exactly
 * the thing this flow exists to avoid, and it surfaced whenever the policy was
 * merely missing — turning a loading failure into a demand.
 */
export function LikeActionForm({objectiveTemplateId, onClaimed}: EarnActionFormProps): React.ReactElement {
    const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
    const like = useCreateEarnSocialLike();
    const {data: policy, isLoading: policyLoading, isError: policyFailed} = useGetEarnActionPolicy(objectiveTemplateId);
    const queryClient = useQueryClient();

    const platforms = policy?.acceptedPlatforms ?? [];
    const targets = policy?.targetPostUrls ?? [];
    const meta = platformMeta(platforms[0] ?? "");

    /*
     * OPENED, PER POST. Stored rather than held in state alone: opening the
     * post leaves the app, and the surface is re-inflated on the way back, so
     * in-memory state would be gone exactly when the member returns to confirm.
     * Keyed by URL so one post's flag never unlocks another's.
     */
    const hasFocus = useHasFocus();
    const [opened, setOpened] = useState<Record<string, boolean>>({});
    const targetKey = targets.join("|");
    useEffect(() => {
        const urls = targetKey ? targetKey.split("|") : [];
        if (!urls.length) return;
        const next: Record<string, boolean> = {};
        for (const url of urls) {
            try {
                next[url] = localStorage.getItem(`earn-like-opened:${url}`) === "1";
            } catch {
                next[url] = false;
            }
        }
        setOpened(next);
    }, [targetKey, hasFocus]);

    const markOpened = (url: string): void => {
        try {
            localStorage.setItem(`earn-like-opened:${url}`, "1");
        } catch { /* private mode — the in-memory flag below still unlocks the confirm. */ }
        setOpened(prev => ({...prev, [url]: true}));
    };

    const claim = async (url: string): Promise<void> => {
        if (!url.trim() || like.isPending) return;
        setOutcome(null);
        try {
            const result = await like.mutateAsync({objectiveTemplateId, postUrl: url.trim()});
            /*
             * capReached is a REFUSAL, not a claim. The route returns 200 with
             * the row already removed, so falling through to the success branch
             * told the member points were coming and fired onClaimed for a
             * credit that does not exist.
             */
            if (result.capReached) {
                setOutcome({tone: "info", text: "You've used all your likes for this period."});
            } else if (result.alreadyClaimed) {
                setOutcome({tone: "info", text: "You've already claimed a like on that post."});
            } else {
                setOutcome({tone: "ok", text: "Claimed — points are on the way."});
                onClaimed?.();
            }
            void queryClient.invalidateQueries({queryKey: earnActionPolicyKey(objectiveTemplateId)});
            void queryClient.invalidateQueries({queryKey: boardEarnObjectivesKey});
        } catch (err) {
            setOutcome({tone: "error", text: await actionErrorMessage(err)});
        }
    };

    /* No policy: nothing to open, nothing to confirm. Say that and stop. */
    if (!policy) {
        return (
            <div className="earn-action earn-action__stack">
                <PolicyStatus isLoading={policyLoading} isError={policyFailed} />
            </div>
        );
    }

    /*
     * A like needs a post. Without one the member would have to supply the
     * URL, and that is the flow we do not run.
     */
    if (!targets.length) {
        return (
            <div className="earn-action earn-action__stack">
                <p className="earn-action__lede">
                    This period&apos;s post hasn&apos;t been set yet. Check back shortly.
                </p>
            </div>
        );
    }

    return (
        <div className="earn-action earn-action__stack">
            <p className="earn-action__lede">
                {policy.instructions ??
                    `Like the post on ${meta.label} to earn points this period. Open it below, hit Like, then come back and confirm.`}
            </p>

            {targets.map((url, i) =>
                <React.Fragment key={url}>
                    <ExternalLink className="earn-action__action" href={url} onClick={() => markOpened(url)}>
                        Open {meta.label} post{targets.length > 1 ? ` ${i + 1}` : ""}
                    </ExternalLink>
                    {/* The button can be swallowed by an in-app browser. */}
                    <p className="earn-action__hint">
                        If the button does nothing, long-press this link and choose Open or Copy:{" "}
                        <ExternalLink href={url} onClick={() => markOpened(url)}>{url}</ExternalLink>
                    </p>
                    {/*
                      * CONFIRM ONLY AFTER OPENING. A member cannot have liked a post
                      * they never opened, and the claim spends their allowance for the
                      * period — so the confirm appears once the post has been opened,
                      * the flow RTTR shipped (MinelabRttrActionModal's opened gate).
                      */}
                    {opened[url]
                        ? <>
                            <p className="earn-action__lede">Done liking the post?</p>
                            <button
                                type="button"
                                className="earn-action__action"
                                onClick={() => void claim(url)}
                                disabled={like.isPending}
                            >
                                {like.isPending ? "Claiming…" : "Yes, I liked it"}
                            </button>
                        </>
                        : <p className="earn-action__hint">Open the post first, then come back to confirm.</p>
                    }
                </React.Fragment>)}

            {outcome && <p className={`earn-action__outcome earn-action__outcome--${outcome.tone}`} role="status">{outcome.text}</p>}
        </div>
    );
}

/**
 * Post claim — post it, paste the link, we check it. The hashtag, the
 * platform and how many count are the assignment's; the form only says
 * them out loud so the member is not guessing at rules the verifier will
 * enforce anyway.
 */
export function PostActionForm({objectiveTemplateId, label, onClaimed}: EarnActionFormProps): React.ReactElement {
    const inputId = useId();
    const [postUrl, setPostUrl] = useState("");
    const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
    const post = useCreateEarnSocialPost();
    const {data: policy, isLoading: policyLoading, isError: policyFailed} = useGetEarnActionPolicy(objectiveTemplateId);
    const queryClient = useQueryClient();

    const platforms = policy?.acceptedPlatforms ?? [];
    const [platform, setPlatform] = useState("");
    useEffect(() => {
        if (platforms.length && !platform) setPlatform(platforms[0]);
    }, [platforms, platform]);

    const meta = platformMeta(platform || platforms[0] || "");
    const hashtag = policy?.requiredHashtags?.[0];
    const cap = policy?.maxClaimsPerPeriod;

    const submit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        const chosen = platform || platforms[0];
        if (!postUrl.trim() || !chosen || post.isPending) return;
        setOutcome(null);
        try {
            const result = await post.mutateAsync({
                objectiveTemplateId,
                postUrl: postUrl.trim(),
                platform: chosen,
            });
            /* A refusal, not a claim — see the like widget above. */
            if (result.capReached) {
                setOutcome({tone: "info", text: "You've used all your submissions for this period."});
            } else if (result.alreadyClaimed) {
                setOutcome({tone: "info", text: "That post has already been claimed."});
            } else {
                setPostUrl("");
                setOutcome({tone: "ok", text: "Verified — points are on the way."});
                onClaimed?.();
            }
            void queryClient.invalidateQueries({queryKey: earnActionPolicyKey(objectiveTemplateId)});
            void queryClient.invalidateQueries({queryKey: boardEarnObjectivesKey});
        } catch (err) {
            setOutcome({tone: "error", text: await actionErrorMessage(err)});
        }
    };

    /*
     * No policy: the platform, the hashtag and the cap are all unknown, so the
     * form would ask for a link against rules it cannot state.
     */
    if (!policy) {
        return (
            <div className="earn-action earn-action__stack">
                <PolicyStatus isLoading={policyLoading} isError={policyFailed} />
            </div>
        );
    }

    return (
        <form className="earn-action earn-action__stack" onSubmit={submit}>
            {hashtag && platforms.length > 0 &&
                <p className="earn-action__heading">Use {asHashtag(hashtag)} on {meta.label}</p>
            }
            <p className="earn-action__lede">
                {policy.instructions ??
                    <>
                        Share your journey on social.
                        {hashtag && <> Use <strong>{asHashtag(hashtag)}</strong> in your {meta.label} post.</>}
                        {" "}Then paste the post's link below.
                        {cap ? ` Up to ${cap} post${cap === 1 ? "" : "s"} count each period.` : ""}
                    </>}
            </p>

            <PlatformPills platforms={platforms} selected={platform} onSelect={setPlatform} />

            <input
                id={inputId}
                className="earn-action__field"
                value={postUrl}
                onChange={e => setPostUrl(e.target.value)}
                placeholder={`Paste your ${meta.label} URL here — e.g. ${meta.example}`}
                aria-label={`Your ${meta.label} post URL`}
                inputMode="url"
                autoComplete="off"
            />
            <button
                className="earn-action__action"
                type="submit"
                disabled={!postUrl.trim() || !(platform || platforms[0]) || post.isPending}
            >
                {post.isPending ? "Checking…" : "Submit"}
            </button>

            <RemainingLine remaining={policy.remainingClaims} noun="submission" />
            {outcome && <p className={`earn-action__outcome earn-action__outcome--${outcome.tone}`} role="status">{outcome.text}</p>}
        </form>
    );
}
